import { FunctionComponent, useEffect, useState } from 'react';
import {
    Box,
    Fab,
    Stack,
    Tab,
    Typography,
    useTheme,
    Zoom,
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { MailGraphics } from '@/pageContents/mailConfiguration/mailGraphics/MailGraphics';
import { MailTexts } from '@/pageContents/mailConfiguration/mailTexts/MailTexts';
import {
    useGetMailTextsQuery,
    usePostMailTextMutation,
} from '@/api/v2/mailConfiguration';
import { MailText } from '@/models/mailConfiguration';
import { useNotification } from '@starjack/features/notifications';
import { useSlice } from '@/hooks/useSlice';
import { pageDataSlice } from '@starjack/features/pageData';

export const MailConfigurationContent: FunctionComponent = () => {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState('graphics');
    const [newMailText, setNewMailText] = useState<MailText>(null);
    const { data } = useGetMailTextsQuery();
    const [postMailText] = usePostMailTextMutation();
    const { addNotification } = useNotification();
    const {
        actions: { setPageData },
    } = useSlice(pageDataSlice, 'requestSlice');

    const transitionDuration = {
        enter: theme.transitions.duration.enteringScreen,
        exit: theme.transitions.duration.leavingScreen,
    };

    const handleTabChange = (_event, newValue: string) => {
        setTabValue(newValue);
    };

    const onMailTextSave = () => {
        if (newMailText) {
            postMailText(newMailText)
                .unwrap()
                .then(() => {
                    addNotification({
                        message: 'Mail Texte erfolgreich gespeichert',
                        type: 'success',
                    });
                })
                .catch(() => {
                    addNotification({
                        message:
                            'Fehler beim Speichern. Bitte versuchen Sie es später noch einmal',
                        type: 'error',
                    });
                });
        }
    };

    useEffect(() => {
        setPageData({
            title: 'Mail Konfiguration',
            body: (
                <Box>
                    <Typography>
                        Passen Sie die E-Mail-Korrespondenz der Buchungsstrecke
                        inhaltlich und optisch an.
                    </Typography>
                    <Typography>
                        Ihre individuellen Mailvorlagen werden bei Bestellungen
                        über Ihren starjack-Webshop verwendet. (Nicht bei
                        Bestellungen über das Portal starjack.at).
                    </Typography>
                </Box>
            ),
        });
    }, []);

    return (
        <TabContext value={tabValue}>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Mail Konfiguration
            </Typography>
            <Stack spacing={theme.page.verticalSpacing}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabList onChange={handleTabChange}>
                        <Tab label='Grafiken bearbeiten' value='graphics' />
                        <Tab label='Mail Texte bearbeiten' value='texts' />
                    </TabList>
                </Box>

                <TabPanel value='graphics'>
                    <MailGraphics />
                </TabPanel>

                <TabPanel value='texts'>
                    <MailTexts
                        initialMailTexts={data}
                        onMailTextsUpdate={setNewMailText}
                    />
                </TabPanel>

                {/*
                    The mail-texts save button is placed here (outside of the actual component) to enable a smooth transition animation
               */}
                <Zoom
                    in={tabValue === 'texts'}
                    timeout={transitionDuration}
                    style={{
                        transitionDelay: `${
                            tabValue === 'texts' ? transitionDuration.enter : 0
                        }ms`,
                    }}
                    unmountOnExit
                >
                    <Fab
                        onClick={onMailTextSave}
                        sx={{
                            position: 'fixed',
                            right: 32,
                            bottom: 32,
                        }}
                        variant='extended'
                        color='primary'
                    >
                        Speichern
                    </Fab>
                </Zoom>
            </Stack>
        </TabContext>
    );
};
